import React from 'react';
import { CompleteKundali } from '../../types/kundali';
import { getPlanetFullName } from '../../utils/language';
import { GranthaCommonProps } from './Grantha1_JanmaGanita';

export const Grantha5_Phaladesh: React.FC<GranthaCommonProps> = ({
  kundali,
  lang,
  pageBorder,
  runningHeaderBorder,
  headingPrimary,
  headingAccent,
  innerCardBg,
  tableBorder,
  tableHeaderBg,
  tableRowEven,
  tableRowOdd,
  mutedText,
  renderPageFooter
}) => {
  const isHi = lang === 'hi' || lang === 'sa';
  const { birthDetails, planets = [] } = kundali;

  const planetPairs = [
    {
      page: 28,
      pageHi: '२८',
      title: 'Sun (Surya) & Moon (Chandra) Dossiers',
      p1: {
        p: 'Sun',
        sloka: 'सूर्योदयस्थे नृपतित्वदायी, केन्द्रत्रिकोणे यदि भानुयुक्तः।\nदीर्घायुषं कीर्तिकरं प्रशस्तं, प्रतापवन्तं कुलदीपकं च॥',
        meaning: isHi ? 'केन्द्र अथवा त्रिकोण में बली सूर्य जातक को कुलदीपक, तेजस्वी, दीर्घायु एवं राजसम्मान प्रदान करता है।' : 'When the Sun is placed in angles or trines, the native attains royal honors, vitality, and illustrious status.',
        analysis: 'The Sun governs the soul, father, executive authority, and internal confidence. Its placement shapes career ambition, leadership acumen, and constitutional vigor.',
        pillar1: 'Strong bone density, robust cardio-vascular vitality, courageous aura and dignified natural posture.',
        pillar2: 'High suitability for administrative leadership, government contracts, corporate management and autonomous enterprise.',
        pillar3: 'Soul lesson involves balancing sovereign ambition with genuine humility and selfless service to lineage.',
        remedy: 'Offer Arghya to the rising Sun daily with red copper vessel; recite Aditya Hridaya Stotram on Sundays.'
      },
      p2: {
        p: 'Moon',
        sloka: 'चन्द्रो यदि स्याद् बलवान् सुदृष्टः, कान्तं वपुश्चित्तसुखं ददाति।\nविद्याविनोदं धनधान्यवृद्धिं, मातुल्यवात्सल्यकरं प्रशान्तम्॥',
        meaning: isHi ? 'बली चन्द्रमा चित्त को प्रसन्न, शरीर को कान्तिमान, बुद्धि को प्रखर एवं जीवन को वात्सल्य और सौम्यता से परिपूर्ण करता है।' : 'A potent Moon bestows radiant magnetism, serene equilibrium, scholarly delight, and emotional prosperity.',
        analysis: 'The Moon governs the emotional mind (Manas), intuition, mother, and public popularity. Its lunar mansion dictates psychological resilience.',
        pillar1: 'Fluid constitutional balance, magnetic eyes, receptive nervous system and natural psychosomatic calmness.',
        pillar2: 'Excellence in hospitality, public relations, trade involving liquids, psychological counselling and mass communication.',
        pillar3: 'Soul lesson involves navigating emotional tides without attachment and cultivating inner meditative stillness.',
        remedy: 'Honor maternal figures, drink water in pure silver tumbler, meditate on Moonlit Mondays and recite Chandra Gayatri.'
      }
    },
    {
      page: 29,
      pageHi: '२९',
      title: 'Mars (Mangal) & Mercury (Budha) Dossiers',
      p1: {
        p: 'Mars',
        sloka: 'भौमः सुकेन्द्रे जयदः समरे, पराक्रमं शौर्यबलं च धत्ते।\nभूमिप्रदः साहसिकः प्रतापी, सेनापतिः शूरवरो नराणाम्॥',
        meaning: isHi ? 'केन्द्र में बली मंगल युद्ध व स्पर्धा में विजय, अचल संपत्ति, शौर्य एवं अदम्य साहस प्रदान करता है।' : 'Angular or exalted Mars guarantees triumph in competitive trials, real estate wealth, and decisive bravery.',
        analysis: 'Mars represents biological drive, physical stamina, real estate assets, and executive initiative.',
        pillar1: 'High muscular stamina, swift metabolic rate, sharp reflexes, and indomitable recuperative physical strength.',
        pillar2: 'Supreme aptitude for engineering, property development, surgical medicine, sports, and competitive management.',
        pillar3: 'Soul lesson focuses on transforming impulsive friction into constructive disciplined courage.',
        remedy: 'Recite Hanuman Chalisa daily; distribute sweet jaggery on Tuesdays; avoid harsh speech during meals.'
      },
      p2: {
        p: 'Mercury',
        sloka: 'बुधो यदि स्याद् गुणवान् प्रवीणी, शास्त्रार्थदक्षः सुवचा विपश्चित्।\nवाणिज्यविद्यासु च कीर्तियुक्तो, मिताशिनं बुद्धिमतं च कुर्यात्॥',
        meaning: isHi ? 'बुध वाणी का माधुर्य, तीक्ष्ण प्रज्ञा, व्यापारिक सफलता एवं शास्त्रों का गहन पाण्डित्य प्रदान करता है।' : 'Mercury grants eloquence of speech, analytical intellect, commercial success, and versatile scholarly learning.',
        analysis: 'Mercury rules analytical intellect, commercial negotiation, verbal dexterity, and mathematical systems.',
        pillar1: 'Youthful demeanor, sharp vocal articulation, adaptable nervous system, and rapid cognitive information processing.',
        pillar2: 'Command over commerce, software development, data analytics, journalism, finance, and strategic diplomacy.',
        pillar3: 'Soul lesson involves grounding intellectual cleverness in ethical integrity and steady concentration.',
        remedy: 'Feed soaked whole moong to cows on Wednesdays; chant Vishnu Sahasranama; support maiden daughters.'
      }
    },
    {
      page: 30,
      pageHi: '३०',
      title: 'Jupiter (Guru) & Venus (Shukra) Dossiers',
      p1: {
        p: 'Jupiter',
        sloka: 'जीवो यदि स्याद् भवने प्रशस्ते, धर्मं मतिं दानपरं ददाति।\nवेदान्तविज्ञं गुरुभक्तियुक्तं, पुत्रादिसौख्यं नृपमान्यमेव॥',
        meaning: isHi ? 'बृहस्पति धर्मबुद्धि, वेदान्त ज्ञान, गुरु-भक्ति, सुयोग्य संतान एवं राजसम्मान का वरदान देते हैं।' : 'Jupiter bestows ethical righteousness, spiritual illumination, filial fulfillment, and divine favor.',
        analysis: 'As supreme natural benefic (Devaguru), Jupiter acts as cosmic protector, blessing wisdom, wealth, and dharma.',
        pillar1: 'Robust liver and endocrine vitality, genial disposition, dignified presence and optimistic psychic aura.',
        pillar2: 'Exalted trajectory in higher academia, legal judiciary, advisory directorships, banking, and spiritual institutions.',
        pillar3: 'Soul lesson centers on transmitting sacred wisdom, practicing unconditional charity, and honoring preceptors.',
        remedy: 'Apply pure saffron or turmeric tilak daily; water Peepal tree roots on Thursdays; recite Guru Stotram.'
      },
      p2: {
        p: 'Venus',
        sloka: 'शुक्रो यदि स्याद् रुचिरः कलावान्, शय्यासुखं वाहनभोगदश्च।\nकाव्यप्रवीणो वनिताप्रियश्च, सौन्दर्ययुक्तः सुभगो धनाढ्यः॥',
        meaning: isHi ? 'शुक्र कलात्मक सुरुचि, ऐश्वर्य, उत्तम वाहन, दाम्पत्य सुख एवं भौतिक समृद्धि प्रदान करते हैं।' : 'Venus grants aesthetic taste, conveyances, conjugal harmony, poetic brilliance, and material prosperity.',
        analysis: 'Venus governs beauty, marital happiness, diplomacy, vehicular luxury, and creative prosperity.',
        pillar1: 'Refined symmetry, magnetic social grace, radiant complexion, and healthy reproductive vitality.',
        pillar2: 'Dominance in luxury goods, creative arts, architecture, entertainment, hospitality, and design industries.',
        pillar3: 'Soul lesson involves discerning transcendent spiritual devotion beyond sensory worldly pleasures.',
        remedy: 'Offer white curd and camphor in sanctuary; respect women; chant Sri Suktam on Fridays.'
      }
    },
    {
      page: 31,
      pageHi: '३१',
      title: 'Saturn (Shani) & Rahu Dossiers',
      p1: {
        p: 'Saturn',
        sloka: 'शनिर्यदि स्याद् बलवान् विनीतो, गम्भीरचेताश्चिरजीविकश्च।\nन्यायाधिपो धैर्यसमन्वितश्च, स्थिरप्रज्ञः कार्यकरः समर्थः॥',
        meaning: isHi ? 'बली शनि धैर्य, न्यायप्रियता, दीर्घायु, गम्भीर चिन्तन एवं संगठन क्षमता का सृजन करते हैं।' : 'A strong Saturn cultivates monumental patience, steadfast justice, longevity, and structural mastery.',
        analysis: 'As dispenser of karma (Karmaphala Daata), Saturn rewards relentless perseverance and structural truth.',
        pillar1: 'Enduring skeletal strength, resilient longevity, monumental tolerance to adversity, and steady physical stamina.',
        pillar2: 'Unmatched success in heavy infrastructure, mining, long-term investments, organizational governance, and law.',
        pillar3: 'Soul lesson involves surrender of ego through disciplined humility and dedication to society\'s workers.',
        remedy: 'Light mustard oil lamp under Shami or Peepal tree on Saturdays; perform Chhaya Daan; serve laborers.'
      },
      p2: {
        p: 'Rahu',
        sloka: 'राहुर्यदा स्याद् बलवान् विचित्रे, वैदेशिकं वित्तमथाधिकारम्।\nकौटिल्यशास्त्रेषु च बुद्धिदक्षं, गूढार्थविज्ञं महतोपलब्धिम्॥',
        meaning: isHi ? 'राहु विदेशी संबंध, अप्रत्याशित सफलता, गूढ़ अनुसंधान एवं विशाल महत्वाकांक्षा का कारक है।' : 'Rahu unlocks unconventional breakthroughs, international links, technological horizons, and catalytic worldly ascent.',
        analysis: 'Rahu represents evolutionary ambition, non-linear technological expansion, foreign networks, and sudden breakthroughs.',
        pillar1: 'Heightened electrical nervous energy, acute sensory awareness, and resilience in foreign environments.',
        pillar2: 'Spectacular achievements in cutting-edge tech, international trade, modern media, and disruptive research.',
        pillar3: 'Soul lesson requires distinguishing authentic inner truth from seductive worldly illusions (Maya).',
        remedy: 'Keep pure silver square token in pocket; avoid blue/black bedding; feed street dogs on Saturdays.'
      }
    }
  ];

  const renderPlanetCard = (item: any) => {
    const pPos = planets.find(p => p.name === item.p);
    return (
      <div className={`rounded-xl border p-3 space-y-2 ${innerCardBg}`}>
        {/* Header Title */}
        <div className="flex items-center justify-between border-b pb-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {getPlanetFullName(item.p as any, lang)}
            </span>
            {pPos && (
              <span className="text-[11px] font-mono text-gray-700 bg-white/70 px-2 py-0.5 rounded border">
                {pPos.sign} • H{pPos.house} • {pPos.degreeFormatted || `${pPos.degreeInSign?.toFixed(2)}°`} • {pPos.nakshatra}
              </span>
            )}
          </div>
          <span className="text-[10px] font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
            {pPos?.dignity || 'Dignified'}
          </span>
        </div>

        {/* 2-Column: Sloka & Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
          <div className={`p-2 rounded-lg border ${tableRowOdd} space-y-1 text-center`}>
            <pre className={`text-[10px] font-serif font-bold whitespace-pre-line leading-relaxed ${headingPrimary}`}>
              {item.sloka}
            </pre>
            <p className="text-[10px] italic text-gray-700 border-t pt-0.5 leading-snug">
              {item.meaning}
            </p>
          </div>

          <div className="space-y-1.5 flex flex-col justify-between">
            <p className="text-[11px] text-gray-700 leading-snug">
              {item.analysis}
            </p>
            <div className={`p-1.5 rounded border text-[10px] ${tableRowEven}`}>
              <strong className="text-astro-primary font-serif">Remedy: </strong>
              <span className="italic text-gray-700">{item.remedy}</span>
            </div>
          </div>
        </div>

        {/* Three Pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-[10px] pt-1 border-t">
          <div className="p-1 rounded bg-white/70 border">
            <strong className="text-astro-primary block">1. Vitality:</strong>
            <span className="text-gray-600 leading-tight block">{item.pillar1}</span>
          </div>
          <div className="p-1 rounded bg-white/70 border">
            <strong className="text-astro-primary block">2. Career:</strong>
            <span className="text-gray-600 leading-tight block">{item.pillar2}</span>
          </div>
          <div className="p-1 rounded bg-white/70 border">
            <strong className="text-astro-primary block">3. Soul Lessons:</strong>
            <span className="text-gray-600 leading-tight block">{item.pillar3}</span>
          </div>
        </div>
      </div>
    );
  };

  const ketuItem = {
    p: 'Ketu',
    sloka: 'केतुर्यदा स्याद् मोक्षप्रदाता, अध्यात्मविज्ञं परमात्मनिष्ठम्।\nतपस्विनं सूक्ष्मविवेचकं च, सर्वं विरक्तं जयिनं करोति॥',
    meaning: isHi ? 'केतु मोक्ष, आत्मज्ञान, सूक्ष्म अन्तर्दृष्टि एवं आध्यात्मिक वैराग्य का सर्वोच्च प्रदाता है।' : 'Ketu bestows spiritual liberation, profound intuition, detachment from illusions, and transcendental insight.',
    analysis: 'Ketu represents spiritual liberation (Moksha), esoteric mastery, cellular discernment, and karmic release.',
    pillar1: 'Sharp metaphysical sensitivity, heightened sixth sense, and rapid recovery from chronic ailments.',
    pillar2: 'Mastery over research, cybersecurity, spiritual metaphysics, diagnostic medicine, and deep contemplation.',
    pillar3: 'Soul lesson involves embracing detachment with compassion, achieving freedom from material binding.',
    remedy: 'Feed dual-colored dogs; recite Ganesha Dwadashanama Stotram; donate warm blanket to sadhus.'
  };

  return (
    <>
      {/* ========================================================================= */}
      {/* PAGES 28 TO 31: 4 PAIRED PLANETARY DOSSIER PAGES (2 PLANETS PER PAGE)     */}
      {/* ========================================================================= */}
      {planetPairs.map((pair, idx) => (
        <div key={idx} className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
          <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
            <span>{birthDetails.name}</span>
            <span className="font-serif uppercase tracking-widest">{pair.title}</span>
            <span className="font-mono">{isHi ? `पृष्ठ ${pair.pageHi}` : `Page ${pair.page}`}</span>
          </div>

          <div className="space-y-3 my-auto">
            <div className="text-center space-y-0.5 border-b pb-1">
              <div className={`text-[10px] font-bold uppercase tracking-widest ${headingAccent}`}>
                {isHi ? 'शास्त्रीय फलित एवं भाव-स्थिति समन्वय' : 'SHASTRIC ANALYSIS & CELESTIAL COORDINATES'}
              </div>
              <h2 className={`text-base font-bold font-serif ${headingPrimary}`}>
                {pair.title.toUpperCase()}
              </h2>
            </div>

            {renderPlanetCard(pair.p1)}
            {renderPlanetCard(pair.p2)}
          </div>

          {renderPageFooter(isHi ? `ग्रन्थ ५ • पृष्ठ ${pair.pageHi}` : `Grantha 5 • Page ${pair.page}`)}
        </div>
      ))}

      {/* ========================================================================= */}
      {/* PAGE 32: KETU DOSSIER & 12 BHAVA LIFE DOMAINS OVERVIEW (FULL)             */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ५ • केतु फलित एवं द्वादश भाव प्रवेश' : 'Grantha 5 • Ketu & 12 Bhava Principles'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ३२' : 'Page 32'}</span>
        </div>

        <div className="space-y-3.5 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              KETU (CAUDA DRACONIS) SHASTRIC DOSSIER & LIFE DOMAINS
            </h2>
          </div>

          {renderPlanetCard(ketuItem)}

          {/* 12 Bhava Foundational Framework */}
          <div className={`rounded-xl border p-4 space-y-2 ${tableRowEven}`}>
            <h3 className={`font-bold font-serif uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
              {isHi ? 'द्वादश भाव फलित विचार सिद्धान्त (The 12 Bhava Foundational Canons):' : 'The 12 Bhava Foundational Shastric Canons:'}
            </h3>
            <p className="text-xs text-gray-700 leading-relaxed">
              In classical Parashari Jyotish, life experience manifests through the interaction of Bhavas (the cosmic houses of experience), Bhavadhipatis (the dispositor lords), and Grahas (the energetic conduits residing within or aspecting those cusps). When a bhava is occupied by natural benefics and supported by its lord in Kendra or Trikona, that domain of life yields unassailable fortune, honor, and prosperity.
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ५ • पृष्ठ ३२' : 'Grantha 5 • Page 32')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 33: COMPREHENSIVE 12-BHAVA LIFE DOMAINS SYNTHESIS (100% FILLED PAGE) */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ५ • द्वादश भाव समग्र फलित' : 'Grantha 5 • 12 Bhava Life Domains'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ३३' : 'Page 33'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'द्वादश भाव समग्र फलित एवं जीवन क्षेत्र मीमांसा' : 'COMPREHENSIVE 12-BHAVA LIFE DOMAINS SYNTHESIS'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'जन्म लग्न से व्यय भाव पर्यन्त १२ जीवन क्षेत्रों का संयुक्त शास्त्रीय फलादेश।'
                : 'Complete synthesis of all 12 houses governing health, finance, siblings, family, intellect, career, marriage and liberation.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 text-xs">
            {[
              { h: 1, name: 'Tanu Bhava (Lagna)', title: 'Constitution & Persona', desc: 'Governs physical stamina, vitality, mental disposition, and life orientation.' },
              { h: 2, name: 'Dhana Bhava', title: 'Wealth & Lineage', desc: 'Rules accumulated liquid reserves, vocal eloquence, food intake, and family ties.' },
              { h: 3, name: 'Sahaja Bhava', title: 'Courage & Siblings', desc: 'Signifies initiative, siblings, manual dexterity, communications, and valor.' },
              { h: 4, name: 'Sukha Bhava', title: 'Mother & Real Estate', desc: 'Controls maternal happiness, land, fixed properties, vehicles, and inner peace.' },
              { h: 5, name: 'Putra Bhava', title: 'Intellect & Progeny', desc: 'Governs creative genius, children, higher wisdom, speculative intelligence, and mantras.' },
              { h: 6, name: 'Ari Bhava', title: 'Debts & Daily Service', desc: 'Governs resistance to disease, daily employment, overcoming competitors, and service.' },
              { h: 7, name: 'Kalatra Bhava', title: 'Spouse & Commerce', desc: 'Signifies marital concord, commercial alliances, trade contracts, and public partnerships.' },
              { h: 8, name: 'Randhra Bhava', title: 'Longevity & Secrets', desc: 'Rules occult sciences, unearned inheritance, longevity, and deep psychological shifts.' },
              { h: 9, name: 'Dharma Bhava', title: 'Fortune & Preceptors', desc: 'Controls divine fortune (Bhagya), pilgrimage, father, preceptors, and philosophical destiny.' },
              { h: 10, name: 'Karma Bhava', title: 'Career & Authority', desc: 'Governs professional zenith, royal recognition, public prestige, and executive status.' },
              { h: 11, name: 'Labha Bhava', title: 'Gains & Networks', desc: 'Signifies financial inflows, elder siblings, social networks, and realization of desires.' },
              { h: 12, name: 'Vyaya Bhava', title: 'Moksha & Horizons', desc: 'Rules foreign residence, subconscious detachment, expenditure, and spiritual liberation.' }
            ].map((bhava, bIdx) => (
              <div key={bIdx} className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <div className="flex items-center justify-between border-b pb-0.5 mb-1">
                  <span className="font-bold text-astro-primary">{bhava.name}</span>
                  <span className="text-[10px] font-mono text-gray-500 font-semibold">H{bhava.h}</span>
                </div>
                <span className="font-semibold text-gray-800 text-[11px] block">{bhava.title}</span>
                <p className="text-[10px] text-gray-600 leading-tight mt-0.5">{bhava.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ५ • पृष्ठ ३३' : 'Grantha 5 • Page 33')}
      </div>
    </>
  );
};
